import moment from 'moment';
import { format, utcToZonedTime } from 'date-fns-tz';
import type {
  AppointmentType,
  AppointmentHistoryEntry,
  BuildJsonType,
  BasicContact,
  Timezone,
} from '@bb/domain/dist/types';
import { HOME_INSPECTION_TYPES } from '@app/listing/constants';
import { featureEnabled } from './featureFlagBrowserClient';

export const appointmentActionTaken = (
  {
    status,
    date,
    originalTime,
    actionTaken,
    isPending,
    nonAgentNotified,
    auditEntry,
  }: BuildJsonType<AppointmentHistoryEntry>,
  appointmentType: AppointmentType,
  coop: BuildJsonType<BasicContact>,
  mustCallContacts = false,
  allHistory: BuildJsonType<AppointmentHistoryEntry>[] = [],
  timezone: Timezone,
) => {
  if (status.includes('time_change') && originalTime) {
    const formattedTime = `Original: ${format(
      utcToZonedTime(originalTime, timezone),
      'iii, MMM do yyyy - h:mm a',
    )}`;
    return actionTaken ? `${formattedTime}: ${actionTaken}` : formattedTime;
  }

  if (
    status.includes('confirm') &&
    isPending &&
    mustCallContacts &&
    actionTaken === 'Auto Confirmed'
  ) {
    return 'CALLING LISTING CONTACT TO CONFIRM';
  }

  if (actionTaken?.includes('MUST DELIVER TO CONCIERGE')) {
    const newAction = actionTaken.replace(
      'MUST DELIVER TO CONCIERGE',
      'MUST CALL AND DELIVER TO CONCIERGE',
    );
    return newAction.includes(': Auto Confirmed')
      ? newAction.replace(': Auto Confirmed', '')
      : newAction;
  }

  if (actionTaken === 'Auto Confirmed') {
    if (
      HOME_INSPECTION_TYPES.includes(appointmentType) ||
      appointmentType === 'Home Inspection' ||
      appointmentType === 'Appraisal'
    ) {
      return 'LISTING AGENT TO APPROVE HOME INSPECTOR/APPRAISER';
    }
    if (appointmentType === 'Photographer') {
      return 'LISTING AGENT TO APPROVE PHOTOGRAPHER';
    }
    if (appointmentType === 'Pest Control') {
      return 'LISTING AGENT TO APPROVE PEST CONTROL';
    }
    return 'MUST CALL SHOWING AGENT OFFICE';
  }

  const naNameMap: Record<string, string> = {
    Photographer: 'PHOTOGRAPHER',
    'Pest Control': 'PEST CONTROL',
  };
  if (status === 'non_agent_approval') {
    let naName = naNameMap[appointmentType] || 'HOME INSPECTOR/APPRAISER';
    if (!coop.isNotAgent) {
      naName = 'CUSTOM AGENT';
    }
    return `LISTING AGENT APPROVED ${naName}`;
  }

  const prevNonAgentNotified = allHistory?.some(
    history =>
      history.nonAgentNotified &&
      moment(new Date(history.date)).isBefore(new Date(date)),
  );
  if (
    featureEnabled('nonAgentNotificationFeature') &&
    coop.nonAgentId &&
    // status, isPending, auditEntry to determine if it is an explicit action taken from action page
    ['confirm', 'cancel', 'deny'].includes(status) &&
    isPending &&
    !auditEntry &&
    !prevNonAgentNotified &&
    !nonAgentNotified
  ) {
    let naName = naNameMap[appointmentType] || 'HOME INSPECTOR/APPRAISER';
    if (!coop.isNotAgent) {
      naName = 'CUSTOM AGENT';
    }

    return `LISTING AGENT TO APPROVE ${naName}${
      actionTaken ? ` | ${actionTaken}` : ''
    }`;
  }
  return actionTaken;
};
